import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { NavTreeItem } from "../../../../../components/common/navigation";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { UserRole } from "../../../../../models/user.model";
import { ProfileCompletionRulesFaqDto } from "../models/profileCompletionRulesFaq.model";
import { profileCompletionFaqService } from "../services/profileCompletionFaqService";

export const PROFILE_COMPLETION_FAQ_QUERY_KEY = ["faq", "profile-completion", "rules"] as const;

const ALL_ROLES: UserRole[] = [UserRole.User, UserRole.Manager, UserRole.Admin];

// Shared React Query result — reused by the tree builder below and by the routed
// overview/rule-detail components so a rule lookup never triggers a second fetch.
export const useProfileCompletionFaqRulesQuery = (): UseQueryResult<ProfileCompletionRulesFaqDto> =>
    useQuery({
        queryKey: PROFILE_COMPLETION_FAQ_QUERY_KEY,
        queryFn: () => profileCompletionFaqService.getRules(),
        staleTime: 15 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

// This topic's useTopicTree() implementation. Everything (explanatory copy + every
// section's every rule) renders on the single /faq/profile-completion page — so this
// topic is a single leaf item in the tree, no per-section/per-rule children to click into.
export const useProfileCompletionFaqTree = (): NavTreeItem | null => {
    const { t } = useTranslation();
    const { data } = useProfileCompletionFaqRulesQuery();

    if (!data) {
        return null;
    }

    return {
        id: "profile-completion",
        label: t("FAQ_TOPIC_PROFILE_COMPLETION", undefined, "Profile Completion"),
        path: "profile-completion",
        requiredRoles: ALL_ROLES,
    };
};
